/**
 * Vitals field helpers — restored Jun 2026.
 * The `client_vitals` form field is a READ-ONLY "Most Recent Vitals"
 * snapshot display (1:1 with original behavior: shows the client's most
 * recent chart vitals, frozen on the note at save time), plus an
 * AddVitalsDialog that records a NEW reading to the client's chart
 * vitals history from within a note.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize a saved note snapshot (JSON string, object, or legacy array)
// into a single vitals object. Returns null when nothing usable.
export const parseVitalsSnapshot = (raw) => {
  let v = raw;
  if (typeof v === 'string') {
    if (!v.trim()) return null;
    try { v = JSON.parse(v); } catch { return null; }
  }
  if (Array.isArray(v)) v = v[0] || null;
  if (!v || typeof v !== 'object') return null;
  return v;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch (e) {
    return dateStr;
  }
};

const formatHeight = (feet, inches) => {
  if (feet == null && inches == null) return 'N/A';
  return `${feet || 0}'${inches || 0}"`;
};

const formatBP = (systolic, diastolic) => {
  if (systolic == null || diastolic == null) return 'N/A';
  return `${systolic}/${diastolic} mmHg`;
};

// 1:1 restoration of the original "Most Recent Vitals" red display box.
export const MostRecentVitalsCard = ({ vitals }) => (
  <div className="border rounded p-3 bg-red-50 border-red-200" data-testid="vitals-field-widget">
    {vitals ? (
      <div className="space-y-2">
        <div className="text-xs text-red-600 mb-2">
          Recorded: {formatDate(vitals.recorded_at)}
          {vitals.recorded_by_name && ` by ${vitals.recorded_by_name}`}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="font-semibold text-red-800">Blood Pressure:</span>{' '}
            {formatBP(vitals.blood_pressure_systolic, vitals.blood_pressure_diastolic)}
          </div>
          <div>
            <span className="font-semibold text-red-800">Pulse:</span>{' '}
            {vitals.pulse != null ? `${vitals.pulse} bpm` : 'N/A'}
          </div>
          <div>
            <span className="font-semibold text-red-800">O2 Saturation:</span>{' '}
            {vitals.oxygen_saturation != null ? `${vitals.oxygen_saturation}%` : 'N/A'}
          </div>
          <div>
            <span className="font-semibold text-red-800">Respiratory:</span>{' '}
            {vitals.respiratory_rate != null ? `${vitals.respiratory_rate} rpm` : 'N/A'}
          </div>
          <div>
            <span className="font-semibold text-red-800">Temperature:</span>{' '}
            {vitals.temperature != null ? `${vitals.temperature}°F` : 'N/A'}
          </div>
          <div>
            <span className="font-semibold text-red-800">Height:</span>{' '}
            {formatHeight(vitals.height_feet, vitals.height_inches)}
          </div>
          <div>
            <span className="font-semibold text-red-800">Weight:</span>{' '}
            {vitals.weight != null ? `${vitals.weight} lbs` : 'N/A'}
          </div>
        </div>
        {vitals.notes && (
          <div className="text-xs mt-2 pt-2 border-t border-red-200">
            <span className="font-semibold text-red-800">Notes:</span> {vitals.notes}
          </div>
        )}
      </div>
    ) : (
      <p className="text-xs text-red-600 italic">No vitals recorded for this client</p>
    )}
  </div>
);

const INT_KEYS = ['blood_pressure_systolic', 'blood_pressure_diastolic', 'pulse', 'respiratory_rate', 'height_feet', 'height_inches'];
const FLOAT_KEYS = ['temperature', 'oxygen_saturation', 'weight'];

const NUM_FIELDS = [
  { key: 'pulse', label: 'Heart Rate (bpm)', testid: 'vitals-pulse' },
  { key: 'respiratory_rate', label: 'Resp. Rate (/min)', testid: 'vitals-resp-rate' },
  { key: 'temperature', label: 'Temperature (°F)', step: '0.1', testid: 'vitals-temperature' },
  { key: 'oxygen_saturation', label: 'O₂ Saturation (%)', step: '0.1', testid: 'vitals-o2-sat' },
  { key: 'weight', label: 'Weight (lbs)', step: '0.1', testid: 'vitals-weight' },
];

// "Add Vitals" button + dialog — records a NEW vitals reading to the
// client's chart history (POST /clients/{id}/vitals), then hands the
// created reading back via onSaved so the note snapshot can refresh.
export const AddVitalsDialog = ({ clientId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    const payload = {};
    const hasVal = (k) => form[k] !== undefined && form[k] !== null && form[k] !== '';
    INT_KEYS.forEach(k => { if (hasVal(k)) payload[k] = parseInt(form[k]); });
    FLOAT_KEYS.forEach(k => { if (hasVal(k)) payload[k] = parseFloat(form[k]); });
    if (form.notes) payload.notes = form.notes;
    if (form.recorded_at) {
      const d = new Date(form.recorded_at);
      if (!isNaN(d.getTime())) payload.recorded_at = d.toISOString();
    }
    if (Object.keys(payload).filter(k => k !== 'notes').length === 0) {
      toast.error('Please enter at least one vital sign or measurement');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/vitals`, payload);
      const reading = res.data?.reading || res.data;
      toast.success('Vitals recorded and saved to client chart');
      setOpen(false);
      setForm({});
      onSaved?.(reading);
    } catch (e) {
      console.error('Failed to record vitals:', e);
      toast.error(e.response?.data?.detail || 'Failed to record vitals');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 border-red-300 text-red-700 hover:bg-red-50"
        data-testid="add-vitals-from-note-btn"
        onClick={() => {
          const d = new Date();
          d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
          setForm({ recorded_at: d.toISOString().slice(0, 16) });
          setOpen(true);
        }}
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Vitals
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-vitals-dialog">
          <DialogHeader>
            <DialogTitle>Record New Vitals</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Date &amp; time taken</Label>
              <Input
                type="datetime-local"
                value={form.recorded_at ?? ''}
                onChange={(e) => set('recorded_at', e.target.value)}
                data-testid="vitals-recorded-at"
              />
              <p className="text-[11px] text-gray-500 mt-0.5">Defaults to now — change it if these vitals were taken on a different day.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs text-slate-600">Blood Pressure (mmHg)</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number" inputMode="numeric" placeholder="Sys"
                    value={form.blood_pressure_systolic ?? ''}
                    onChange={(e) => set('blood_pressure_systolic', e.target.value)}
                    data-testid="vitals-bp-systolic"
                  />
                  <span className="text-gray-400">/</span>
                  <Input
                    type="number" inputMode="numeric" placeholder="Dia"
                    value={form.blood_pressure_diastolic ?? ''}
                    onChange={(e) => set('blood_pressure_diastolic', e.target.value)}
                    data-testid="vitals-bp-diastolic"
                  />
                </div>
              </div>
              {NUM_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-600">{f.label}</Label>
                  <Input
                    type="number" inputMode="decimal" step={f.step || '1'}
                    value={form[f.key] ?? ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={f.testid}
                  />
                </div>
              ))}
              <div>
                <Label className="text-xs text-slate-600">Height</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number" inputMode="numeric" placeholder="ft"
                    value={form.height_feet ?? ''}
                    onChange={(e) => set('height_feet', e.target.value)}
                    data-testid="vitals-height-feet"
                  />
                  <Input
                    type="number" inputMode="numeric" placeholder="in"
                    value={form.height_inches ?? ''}
                    onChange={(e) => set('height_inches', e.target.value)}
                    data-testid="vitals-height-inches"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Notes</Label>
              <Input
                value={form.notes ?? ''}
                placeholder="Optional context (position, cuff size, etc.)"
                onChange={(e) => set('notes', e.target.value)}
                data-testid="vitals-notes"
              />
            </div>
            <p className="text-[11px] text-gray-500">
              This reading will be saved to the client's chart vitals history and shown on this note.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="add-vitals-cancel-btn">
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving} data-testid="add-vitals-save-btn">
              {saving ? 'Saving...' : 'Save Vitals'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
